/**
 * The staff role / permission constants, from the original's core-enums module
 * **`77498`** (mined out of `recovery-source/app-formatted.js` around the
 * `USER_ROLES_ENUM` / `USER_PERMISSIONS_ENUM` definitions).
 *
 * Only what the STOREFRONT reads is here. The platform's permission enum has
 * around a hundred entries and the admin owns them; the web app checks exactly
 * six, listed in §4.2 of `recovery-source/SPEC_staff-login.md`. Copying the
 * rest in would be dead surface that drifts.
 */

/** `PLATFORM_TYPE_ENUM` — the values `staff.allowed_platforms` holds. */
export const PLATFORM_TYPE_ENUM = {
  WEB: "WEB",
  POS: "POS",
  ADMIN: "ADMIN",
  REPORTS: "REPORTS",
  RIDER: "RIDER",
} as const;

/** `USER_ROLES_ENUM`, in the original's own order. `ADMINSTRATOR` is theirs. */
export const USER_ROLES_ENUM = {
  SUPER_ADMIN: "SUPER_ADMIN",
  MODERATOR: "MODERATOR",
  SUPPORT_SPECIALIST: "SUPPORT_SPECIALIST",
  ADMINSTRATOR: "ADMINSTRATOR",
  MANAGER: "MANAGER",
  EDITOR: "EDITOR",
  CONTRIBUTOR: "CONTRIBUTOR",
  ANALYST: "ANALYST",
  SALES_PERSON: "SALES_PERSON",
  CASHIER: "CASHIER",
  STAFF: "STAFF",
  DRIVER: "DRIVER",
} as const;

export type UserRole = (typeof USER_ROLES_ENUM)[keyof typeof USER_ROLES_ENUM];

/**
 * The six permissions the storefront checks, and nothing else.
 *
 * | key | what it gates |
 * |---|---|
 * | `POS_VIEW_REPORTS` | the Account menu's **X Report** and **Staff Orders** entries |
 * | `POS_DELETE_REMOVE_ORDER_ITEM` | the trash control in the edit-order-item modal |
 * | `POS_CREATE_ORDER` | `"You do not have permission to create orders"` |
 * | `POS_UPDATE_ORDER` | `"You do not have permission to update orders"` |
 * | `POS_UPDATE_ASSIGN_ORDER_TAKER` | the Assign-Order-Taker row on all three summary bars |
 * | `POS_CREATE_APPLY_TIP` | the "Add a tip?" block, for staff only |
 */
export const USER_PERMISSIONS_ENUM = {
  POS_VIEW_REPORTS: "POS_VIEW_REPORTS",
  POS_DELETE_REMOVE_ORDER_ITEM: "POS_DELETE_REMOVE_ORDER_ITEM",
  POS_CREATE_ORDER: "POS_CREATE_ORDER",
  POS_UPDATE_ORDER: "POS_UPDATE_ORDER",
  POS_UPDATE_ASSIGN_ORDER_TAKER: "POS_UPDATE_ASSIGN_ORDER_TAKER",
  POS_CREATE_APPLY_TIP: "POS_CREATE_APPLY_TIP",
} as const;

export type UserPermission =
  (typeof USER_PERMISSIONS_ENUM)[keyof typeof USER_PERMISSIONS_ENUM];

/**
 * `ALL_MODERATOR_PERMISSIONS`, verbatim.
 *
 * A `MODERATOR` is checked against this fixed list rather than against the
 * tenant's role matrix, so it has to be reproduced exactly — and note what it
 * does NOT contain: none of the six above. A moderator therefore has no
 * storefront permission at all, which is the behaviour, not an omission.
 */
export const ALL_MODERATOR_PERMISSIONS: readonly string[] = [
  "ADMIN_VIEW_MENU",
  "ADMIN_VIEW_INVENTORY",
  "ADMIN_VIEW_OFFERS",
  "ADMIN_VIEW_QR_CODES",
  "ADMIN_VIEW_DELIVERY_AREAS",
  "ADMIN_VIEW_STORE_SHIFTS",
  "ADMIN_VIEW_DRIVERS",
  "ADMIN_VIEW_STORE_SETTINGS",
  "ADMIN_VIEW_STAFF_SHIFTS",
  "ADMIN_VIEW_DISCOUNTS",
  "ADMIN_CREATE_MENU",
  "ADMIN_CREATE_INVENTORY",
  "ADMIN_CREATE_OFFER",
  "ADMIN_CREATE_DELIVERY_AREAS",
  "ADMIN_CREATE_STORE_SHIFT",
  "ADMIN_CREATE_DRIVERS",
  "ADMIN_CREATE_STAFF_SHIFT",
  "ADMIN_CREATE_DISCOUNT",
  "ADMIN_UPDATE_MENU",
  "ADMIN_UPDATE_OFFER",
  "ADMIN_UPDATE_DELIVERY_AREA",
  "ADMIN_UPDATE_STORE_SHIFT",
  "ADMIN_UPDATE_STORE_SETTINGS",
  "ADMIN_UPDATE_STAFF_SHIFT",
  "ADMIN_UPDATE_DISCOUNT",
  "ADMIN_DELETE_MENU",
  "ADMIN_DELETE_CUSTOMER",
  "ADMIN_DELETE_OFFER",
  "ADMIN_DELETE_DELIVERY_AREA",
  "ADMIN_DELETE_DRIVER",
  "ADMIN_DELETE_STORE_SHIFT",
  "ADMIN_DELETE_RESERVATION",
  "ADMIN_DELETE_DISCOUNT",
  "POS_VIEW_MENU",
  "POS_VIEW_ORDERS",
  "POS_VIEW_DRIVERS",
  "POS_VIEW_CASHIER",
  "POS_VIEW_STORE_SETTINGS",
  "POS_CREATE_PRINT_DUPLICATE_DOCKET",
  "POS_VIEW_PRINTER_SETTINGS",
];
